import Button from "@/components/Button";
import { NewTag } from "@/components/Tag";
import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import FC24Player from "@/components/molecules/Player/FC24/FC24Player";
import { PlusPlus } from "@/components/molecules/PlusPlus/PlusPlus";
import { canonicalUrl } from "@/config/canonical";
import { PINNED_ASSET_SLUG } from "@/config/game";
import { usePlayerCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import {
  CompareOrigin,
  trackAddedPlayerToCompare,
} from "@/events/compareMixpanel";
import { useGoBack } from "@/hooks/useGoBack";
import { useEscape } from "@/hooks/useKeyboardShortcut";
import { MetaRatingIcon } from "@/icons";
import { owned_icon_true as ownedIcon } from "@/icons";
import { cn } from "@/lib/utils";
import { contentLabelsSelector } from "@/redux/loadedContent/loadedContentSlice";
import {
  getRolesPlusOrPlusPlus,
  playerRoleLabelSelector,
  tierSelector,
} from "@/redux/players/playersSlice";
import { chemStylesToIcon } from "@/redux/squadBuilder/squadBuilderUtils";
import { BREAKPOINTS } from "@/settings";
import { useTranslation } from "@repo/shared/i18n";
import {
  addComparePlayer,
  comparePlayerKey,
  comparePlayersSelector,
  isInCompareSelector,
  removeComparePlayer,
} from "@repo/shared/redux";
import { Check, Plus, Save, Share } from "lucide-react";
import { Oval } from "react-loading-icons";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { DetailedPlayerType } from "../../DetailedPlayer";
import { Chemistry } from "../../reducer";
import AlternativeVersionsSection, {
  AlternativeVersion,
  AlternativeVersionType,
} from "../AlternativeVersionsSection";
import { ChemistryIcon } from "../Attributes/Chemistry/ChemistrySection";
import { CurrentState } from "../CurrentState";
import { EvolutionsButton } from "../Evolutions/EvolutionsButton";
import { MobileScrollTopSection } from "./MobileScrollTopSection";

export interface TopSectionProps {
  player: DetailedPlayerType;
  metaRating: number;
  metaRatingZeroChem: number;
  chemistry: Chemistry;
  chemStyleId: number;
  playerRoleId: number;
  rolesPlus: number[];
  name: string;
  versionName?: string;
  scrollPastTopBar: boolean;
  isEvolution: boolean;
}

export const TopSection = ({
  player,
  metaRating,
  metaRatingZeroChem,
  chemistry,
  chemStyleId,
  playerRoleId,
  rolesPlus,
  name,
  versionName,
  scrollPastTopBar,
  isEvolution,
}: TopSectionProps) => {
  // Closes the card to wherever it was opened from — /players, /squad-builder,
  // /my-club/players, … — or the route parent on a shared link.
  const goBack = useGoBack();

  useEscape(goBack);
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);

  // player is undefined until the detail request resolves, so both fields are
  // read optionally — the copy renders without them and fills in on arrival.
  const playerHelmet = usePlayerCopy({
    name,
    versionName,
    rating: player?.rating,
    gender: player?.gender,
  });

  return (
    <HeroSection
      helmetTitle={playerHelmet.title}
      pageTitle={playerHelmet.pageTitle}
      helmetDescription={playerHelmet.description}
      helmetCanonicalLink={canonicalUrl()}
      backgroundImageUrl={
        import.meta.env.VITE_APP_ASSETS_BASE_URL +
        `/${PINNED_ASSET_SLUG}/page-headers/detailed-player-page.png`
      }
      back={goBack}
      {...(scrollPastTopBar
        ? {
            alternativeHeader: (
              <MobileScrollTopSection
                player={player}
                metaRating={metaRating}
                metaRatingZeroChem={metaRatingZeroChem}
                scrollPastTopBar={scrollPastTopBar}
                chemistry={chemistry}
                chemStyleId={chemStyleId}
                playerRoleId={playerRoleId}
                rolesPlus={rolesPlus}
                name={name}
                onClose={goBack}
                isEvolution={isEvolution}
              />
            ),
          }
        : {})}
    >
      <div className="bg-new-gray-900 absolute top-0 left-0 right-0 w-full h-full">
        <img
          className="h-full opacity-10 touch-device:opacity-50 touch-device:h-[228px] object-left touch-device:object-center object-cover mx-auto"
          src={
            import.meta.env.VITE_APP_ASSETS_BASE_URL +
            `/${PINNED_ASSET_SLUG}/page-headers/detailed-player-page.png`
          }
          // eslint-disable-next-line i18next/no-literal-string -- asset id, not copy
          alt="detailed-player-page-header"
        />
      </div>
      {/*
        `relative` is load-bearing: the header art above is absolutely
        positioned, so static content would paint underneath it. The stats used
        to escape that through their panel's backdrop-blur, which is gone.
      */}
      <div className="relative z-10 h-[152px] mobile:h-[228px] flex flex-row mobile:grid grid-cols-4 max-w-content mx-auto w-full px-4 touch-device:px-10 large-desktop:px-20 ">
        {/*
          Between the mobile and touch-device breakpoints the column is wide
          for the card it holds, which leaves it sitting against the stats
          plate. Nudged left across that band and put back at touch-device,
          where the column grows. A transform, so the column keeps its width.
        */}
        <div className="col-span-1 h-[175px] touch-device:h-[300px] -translate-y-4 mobile:translate-y-4 mobile:-translate-x-6 touch-device:translate-x-0 touch-device:translate-y-0">
          <div className="h-full w-[100px] mobile:w-[210px] mx-auto flex items-center justify-center">
            {player?.attributes && (
              <div
                className={cn(
                  "scale-115 mobile:scale-160 touch-device:scale-140 transition-opacity duration-200 ease-in-out",
                  scrollPastTopBar && !isMobile ? "opacity-0" : "opacity-100",
                )}
              >
                <FC24Player
                  {...player}
                  chemStyleId={0}
                  isSbcPlayer={false}
                  isObjectivePlayer={false}
                  untradeable={false}
                  isMobile={false}
                  largeMobile={true}
                  hidePrice={true}
                  isEvoPlayer={isEvolution}
                  // Not while a card drawn from list data waits on the full
                  // player: that render would land mid-pass.
                  playOnShow={!!player.metaRatings}
                />
              </div>
            )}
          </div>
        </div>
        {/*
          Phones give the stats the whole right side and let the grid set its
          own height; from the mobile breakpoint up it goes back to the fixed
          single-row plate.
        */}
        <div className="col-span-3 flex-1 min-w-0 flex items-center pl-4 mobile:pl-0 mobile:h-full mobile:mt-0 max-h-54 mobile:translate-y-6">
          <CurrentState
            metaRating={metaRating}
            chemStyleId={chemStyleId}
            playerRoleId={playerRoleId}
            rolesPlus={rolesPlus}
            metaRatingZeroChem={metaRatingZeroChem}
            price={{
              priceInfo: player?.priceInfo,
              objectiveDisplay: player?.objectiveDisplay,
            }}
            itemScore={player?.itemScore}
          />
        </div>
      </div>
    </HeroSection>
  );
};

export const TopSectionCompressed = ({
  playerName,
  metaRating,
  scrollPastTopBar,
  metaRatingZeroChem,
  playerRoleId,
  version,
  owned,
}: {
  playerName: string;
  metaRating: number;
  scrollPastTopBar: boolean;
  metaRatingZeroChem: number;
  playerRoleId: number;
  version: AlternativeVersionType;
  owned: boolean;
}) => {
  const tier = useSelector(tierSelector(metaRatingZeroChem, playerRoleId));
  return (
    <>
      <div
        className={cn(
          "text-sm mobile:text-base font-semibold text-dark-primary transition-opacity duration-100 flex flex-row items-center justify-between  gap-x-3 w-full pr-14",
          scrollPastTopBar ? "opacity-100" : "opacity-0",
        )}
      >
        <div className="flex flex-row items-center gap-x-1">
          <AlternativeVersion
            version={version}
            playerName={playerName}
            isActive={true}
          />
          <p className="whitespace-nowrap ml-1">{playerName}</p>
          {owned && <div className="w-4 h-4 mr-2">{ownedIcon}</div>}
        </div>
        <div className="flex flex-row items-center gap-x-2 text-dark-tertiary">
          <p className="text-sm text-dark-primary font-semibold">
            {metaRating?.toFixed(1)}
          </p>
          <MetaRatingIcon width="16" height="16" />
          <div className="w-0.5 h-5 bg-dark-disabled rounded-md" />
          <div className="flex flex-row items-center gap-x-1 text-dark-tertiary w-4">
            <p className={cn("text-base font-bold", tier?.tierTextColor)}>
              {tier?.tierName}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export const DetailedPlayerToolbar = ({
  player,
  evolutionPath,
  pathwayPath,
  playerRoleId,
  resourceId,
  chemStyleId,
  chemistry,
  scrollPastTopBar,
  setIsAlternativeVersionsOpenMobile,
  isAlternativeVersionsOpenMobile,
  metaRating,
  metaRatingZeroChem,
  playerName,
  rolesPlus,
  handleClickedEvolved,
  handleClickedDownloadAssets,
  handleClickedSaveEvolution,
  savingEvolution,
  isEvolutionPlayer,
  isFromEvolutionBuilder,
  compareOrigin,
}: {
  player: DetailedPlayerType;
  evolutionPath: number[];
  pathwayPath?: number[][];
  playerRoleId: number;
  chemStyleId: number;
  chemistry: Chemistry;
  resourceId: number;
  scrollPastTopBar: boolean;
  /** Section this player page sits under — stamped on the compare event. */
  compareOrigin: CompareOrigin;
  setIsAlternativeVersionsOpenMobile: (open: boolean) => void;
  isAlternativeVersionsOpenMobile: boolean;
  metaRating: number;
  metaRatingZeroChem: number;
  playerName: string;
  rolesPlus: number[];
  handleClickedEvolved: () => void;
  handleClickedDownloadAssets: () => void;
  handleClickedSaveEvolution: () => void;
  savingEvolution: boolean;
  isEvolutionPlayer: boolean;
  isFromEvolutionBuilder: boolean;
}) => {
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const { t } = useTranslation(["players", "common"]);
  const dispatch = useDispatch();
  const rolesPlusOrPlusPlus = getRolesPlusOrPlusPlus({
    playerRoleId,
    rolesPlus,
  });
  const { chemstyles } = useSelector(contentLabelsSelector);
  const playerRoleLabel = useSelector(
    playerRoleLabelSelector({ playerRoleId: playerRoleId }),
  );
  const comparePlayers = useSelector(comparePlayersSelector);
  const isInCompare = useSelector(
    isInCompareSelector({
      resourceId,
      evoPathId: player?.evoPathId,
      evolutionPath,
      pathwayPath,
    }),
  );
  const handleClickedCompare = () => {
    if (!player) return;
    // Deep copy: Immer freezes dispatched payloads, and a shallow spread would
    // share (and freeze) nested arrays with the page's local player state.
    const comparePlayer = structuredClone({
      ...player,
      resourceId: resourceId,
      positions: player.possiblePositions,
      evolutionPath: evolutionPath,
      pathwayPath,
    });
    if (isInCompare) {
      dispatch(removeComparePlayer(comparePlayer));
      return;
    }
    // Stage at the chem/chemstyle the player view currently shows (0 = no
    // chemstyle picked there — let the slot default apply).
    dispatch(
      addComparePlayer(comparePlayer, {
        chemistry,
        chemstyleId: chemStyleId || undefined,
      }),
    );
    trackAddedPlayerToCompare({
      origin: compareOrigin,
      source: "player_view",
      player_count: comparePlayers.length + 1,
    });
  };

  return isTouchDevice ? (
    <div className="flex flex-row justify-between items-center w-full gap-x-2">
      {scrollPastTopBar ? (
        <div
          className={cn(
            "flex flex-row gap-x-2 w-full h-9 items-center text-xs",
          )}
        >
          <div className="bg-white/5 h-9 px-1 rounded-sm flex flex-row items-center gap-x-1">
            <p>{playerRoleLabel}</p>
            <PlusPlus
              plus={rolesPlusOrPlusPlus === "plus"}
              plusPlus={rolesPlusOrPlusPlus === "plusPlus"}
            />
          </div>
          <div className="bg-white/5 h-9 px-1 rounded-sm flex flex-row items-center gap-x-1">
            <div
              className="text-lg text-dark-primary w-4"
              style={{
                fontFamily: "UltimateTeamIcons",
              }}
            >
              {chemStylesToIcon[chemStyleId]}
            </div>
            <p className="text-dark-primary">{chemstyles[chemStyleId]?.name}</p>
            <ChemistryIcon
              chemistry={chemistry}
              selected={false}
              hovered={false}
            />
          </div>
        </div>
      ) : isFromEvolutionBuilder ? (
        <></>
      ) : (
        <AlternativeVersionsSection
          alternativeVersions={player?.alternativeVersions}
          playerName={player?.name}
          resourceId={resourceId}
          hide={false}
          setIsAlternativeVersionsOpenMobile={
            setIsAlternativeVersionsOpenMobile
          }
          isAlternativeVersionsOpenMobile={isAlternativeVersionsOpenMobile}
        />
      )}
      <div className="flex flex-row gap-x-2">
        {isEvolutionPlayer && (
          <div className="relative">
            <Button
              variant="tertiary"
              onClick={handleClickedSaveEvolution}
              icon={
                savingEvolution ? (
                  <Oval width={20} height={20} />
                ) : (
                  <Save size={20} />
                )
              }
              iconBeforeText
              disabled={savingEvolution}
            />
          </div>
        )}
        <Button
          variant="tertiary"
          onClick={handleClickedCompare}
          icon={isInCompare ? <Check size={20} /> : <Plus size={20} />}
          iconBeforeText
          active={isInCompare}
        />
        <Button
          variant="tertiary"
          onClick={handleClickedDownloadAssets}
          icon={<Share size={20} />}
          iconBeforeText
        />

        <EvolutionsButton
          disabled={false}
          small={scrollPastTopBar}
          onClick={handleClickedEvolved}
        />
      </div>
    </div>
  ) : (
    <div className="w-full px-6 large-desktop:px-4 grid grid-cols-4 gap-4">
      <div className="col-span-1">
        {scrollPastTopBar && (
          <TopSectionCompressed
            playerName={playerName}
            metaRating={metaRating}
            scrollPastTopBar={scrollPastTopBar}
            metaRatingZeroChem={metaRatingZeroChem}
            playerRoleId={playerRoleId}
            owned={player?.owned}
            version={{
              versionId: player?.versionId,
              metalId: player?.metalId,
              rating: player?.rating,
              resourceId: resourceId,
            }}
          />
        )}
      </div>
      <div className="col-span-1">
        {!isFromEvolutionBuilder && (
          <AlternativeVersionsSection
            alternativeVersions={player?.alternativeVersions}
            playerName={player?.name}
            resourceId={resourceId}
            hide={scrollPastTopBar}
            setIsAlternativeVersionsOpenMobile={
              setIsAlternativeVersionsOpenMobile
            }
            isAlternativeVersionsOpenMobile={isAlternativeVersionsOpenMobile}
          />
        )}
      </div>

      <div className="col-span-2 ml-auto my-auto flex flex-row gap-x-2">
        {isEvolutionPlayer && (
          <div className="relative">
            <Tooltip
              content={t("detailed.topSection.saveEvolutionTooltip")}
              delayDuration={100}
            >
              <Button
                variant="tertiary"
                onClick={handleClickedSaveEvolution}
                icon={
                  savingEvolution ? (
                    <Oval width={20} height={20} />
                  ) : (
                    <Save size={20} />
                  )
                }
                iconBeforeText
                label={t("common:save")}
                disabled={savingEvolution}
              />
            </Tooltip>
          </div>
        )}
        <Button
          variant="tertiary"
          onClick={handleClickedCompare}
          icon={isInCompare ? <Check size={20} /> : <Plus size={20} />}
          active={isInCompare}
          iconBeforeText
          label={t("common:compare")}
        />
        <div className="relative">
          <Tooltip
            content={t("detailed.topSection.sharePlayerTooltip")}
            delayDuration={100}
          >
            <Button
              variant="tertiary"
              onClick={handleClickedDownloadAssets}
              icon={<Share size={20} />}
              iconBeforeText
              label={t("common:share")}
            />
          </Tooltip>
        </div>
        <EvolutionsButton disabled={false} onClick={handleClickedEvolved} />
      </div>
    </div>
  );
};
